import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

import { cn } from '../../utils/classnames';

/**
 * Reusable Input component with Tailwind styling
 * Supports various types, states, and custom icons
 */
export const Input = forwardRef(
  (
    {
      type = 'text',
      placeholder,
      value,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      error = false,
      errorMessage,
      className,
      leftIcon,
      rightIcon,
      name,
      id,
      required = false,
      ...props
    },
    ref
  ) => {
    return (
      <div className="w-full">
        <div className="relative w-full">
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-shaded-text">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            type={type}
            name={name}
            id={id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={cn(
              // Base styles
              'w-full h-12 px-4 rounded-full',
              'bg-app-surface border',
              error ? 'border-red-500' : 'border-app-surface',
              'text-[#FEFCEB] text-[14px] placeholder-app-text-secondary',
              'font-proxima',
              'transition-all duration-200',
              // Focus state
              'focus:outline-none focus:border-[#00E7FF]',
              // Disabled state
              'disabled:opacity-50 disabled:cursor-not-allowed',
              // Icon padding
              leftIcon && 'pl-12',
              rightIcon && 'pr-12',
              className
            )}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
              {rightIcon}
            </div>
          )}
        </div>

        {error && errorMessage && (
          <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  name: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
};

export default Input;
